import {
  formatCnpj,
  LOGO_MAX_BYTES,
  ORGANIZATION_STATUS_LABELS,
  organizationDetailSchema,
  PROJECT_PHASE_LABELS,
  projectSchema,
  projectTemplateSchema,
  type OrganizationDetail,
  type OrganizationInput,
  type Project,
  type ProjectCreateInput,
  type ProjectTemplateInput,
} from '@fluxus/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Archive,
  ArrowLeft,
  Building2,
  FolderKanban,
  ImageUp,
  LayoutTemplate,
  Pencil,
  Plus,
  Power,
  Trash2,
  UserRound,
} from 'lucide-react';
import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { PageContainer } from '@/components/layout/PageHeader';
import { EmptyState, ErrorState, LoadingState } from '@/components/states/States';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Dialog } from '@/components/ui/dialog';
import { FormAlert } from '@/components/ui/form';
import { CompanyNav } from '@/features/organizations/CompanyNav';
import { OrganizationForm } from '@/features/organizations/OrganizationForm';
import { ProjectForm } from '@/features/organizations/ProjectForm';
import { plural } from '@/features/templates/plural';
import { TemplateForm } from '@/features/templates/TemplateForm';
import { errorMessage } from '@/lib/api/errors';
import { useAuth, useCan } from '@/lib/auth/session';
import { cn } from '@/lib/cn';

const LOGO_TYPES = ['image/png', 'image/jpeg', 'image/webp'];

/** Empresa cliente na Central de Empresas (PRD §11.2): cadastro, logotipo, projetos e situação. */
export function EmpresaPage() {
  const { organizationId = '' } = useParams();
  const { client } = useAuth();
  const can = useCan();
  const queryClient = useQueryClient();
  const key = ['organizations', 'detail', organizationId];

  const query = useQuery({
    queryKey: key,
    queryFn: ({ signal }) =>
      client.request(`/organizations/${organizationId}`, { schema: organizationDetailSchema, signal }),
  });
  const setDetail = (detail: OrganizationDetail) => {
    queryClient.setQueryData(key, detail);
    void queryClient.invalidateQueries({ queryKey: ['organizations', 'central'] });
  };

  if (query.isPending) {
    return (
      <PageContainer>
        <LoadingState rows={4} label="Carregando a empresa" />
      </PageContainer>
    );
  }
  if (query.isError) {
    return (
      <PageContainer>
        <ErrorState error={query.error} onRetry={() => void query.refetch()} retrying={query.isFetching} />
      </PageContainer>
    );
  }

  const org = query.data;
  const canEdit = can('central-empresas', 'edit');

  return (
    <PageContainer className="max-w-5xl">
      <Link
        to="/empresas"
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-4" aria-hidden />
        Central de Empresas
      </Link>

      <header className="mt-4 flex flex-wrap items-center gap-4">
        <Logo org={org} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-extrabold tracking-tight md:text-[1.75rem]">{org.name}</h1>
            <Badge variant={org.status === 'ACTIVE' ? 'brand' : 'neutral'}>
              {ORGANIZATION_STATUS_LABELS[org.status]}
            </Badge>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            {[org.legalName, org.document ? `CNPJ ${formatCnpj(org.document)}` : null]
              .filter(Boolean)
              .join(' · ') || 'Cadastro sem razão social e CNPJ'}
          </p>
        </div>
      </header>

      <CompanyNav organizationId={org.id} />

      <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,22rem)]">
        <ProjectsSection
          org={org}
          onChanged={() => {
            void query.refetch();
            // A Central (lista e indicadores) também muda com o projeto aberto, editado ou arquivado.
            void queryClient.invalidateQueries({ queryKey: ['organizations', 'central'] });
          }}
        />
        <div className="flex flex-col gap-6">
          <Section title="Cadastro">
            <OrganizationForm
              key={org.id + org.name + (org.legalName ?? '') + (org.document ?? '')}
              initial={org}
              disabled={!canEdit}
              submitLabel="Salvar cadastro"
              onSubmit={async (input: OrganizationInput) => {
                const updated = await client.request(`/organizations/${org.id}`, {
                  method: 'PATCH',
                  body: input,
                  schema: organizationDetailSchema,
                });
                setDetail(updated);
                toast.success('Cadastro salvo');
              }}
            />
          </Section>
          {canEdit ? <LogoSection org={org} onSaved={setDetail} /> : null}
          {can('central-empresas', 'delete') ? <StatusSection org={org} onSaved={setDetail} /> : null}
        </div>
      </div>
    </PageContainer>
  );
}

function Section({
  title,
  children,
  action,
}: {
  title: string;
  children: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <section aria-label={title} className="rounded-lg border border-border bg-surface p-5">
      <div className="mb-4 flex items-center justify-between gap-3">
        <h2 className="text-base font-bold">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

function Logo({ org, size = 'lg' }: { org: OrganizationDetail; size?: 'lg' | 'xl' }) {
  const box = size === 'xl' ? 'size-24' : 'size-16';
  if (org.logoUrl) {
    return (
      <img
        src={org.logoUrl}
        alt={`Logotipo de ${org.name}`}
        className={cn(box, 'shrink-0 rounded-lg border border-border bg-white object-contain p-1.5')}
      />
    );
  }
  return (
    <span
      aria-hidden
      className={cn(
        box,
        'inline-flex shrink-0 items-center justify-center rounded-lg bg-brand-soft text-brand-strong dark:text-brand',
      )}
    >
      <Building2 className="size-7" />
    </span>
  );
}

function LogoSection({
  org,
  onSaved,
}: {
  org: OrganizationDetail;
  onSaved: (d: OrganizationDetail) => void;
}) {
  const { client } = useAuth();
  const input = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  const upload = useMutation({
    meta: { handlesError: true },
    mutationFn: async (file: File) => {
      const form = new FormData();
      form.append('file', file, file.name);
      return client.request(`/organizations/${org.id}/logo`, {
        method: 'PUT',
        body: form,
        schema: organizationDetailSchema,
      });
    },
    onSuccess: (detail) => {
      onSaved(detail);
      toast.success('Logotipo atualizado');
    },
    onError: (err) => setError(errorMessage(err)),
  });
  const remove = useMutation({
    meta: { handlesError: true },
    mutationFn: () =>
      client.request(`/organizations/${org.id}/logo`, { method: 'DELETE', schema: organizationDetailSchema }),
    onSuccess: (detail) => {
      onSaved(detail);
      toast.success('Logotipo removido');
    },
    onError: (err) => setError(errorMessage(err)),
  });

  function pick(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = '';
    setError(null);
    if (!file) return;
    if (!LOGO_TYPES.includes(file.type)) return setError('Use uma imagem PNG, JPG ou WebP.');
    if (file.size > LOGO_MAX_BYTES) return setError('O logotipo pode ter no máximo 2 MB.');
    upload.mutate(file);
  }

  return (
    <Section title="Logotipo">
      <div className="flex items-center gap-4">
        <Logo org={org} size="xl" />
        <div className="flex flex-col items-start gap-2">
          <input
            ref={input}
            type="file"
            accept={LOGO_TYPES.join(',')}
            className="sr-only"
            tabIndex={-1}
            onChange={pick}
            aria-label="Arquivo do logotipo"
          />
          <Button
            variant="outline"
            size="sm"
            loading={upload.isPending}
            onClick={() => input.current?.click()}
          >
            {upload.isPending ? null : <ImageUp aria-hidden />}
            {org.logoUrl ? 'Trocar logotipo' : 'Enviar logotipo'}
          </Button>
          {org.logoUrl ? (
            <Button variant="ghost" size="sm" loading={remove.isPending} onClick={() => remove.mutate()}>
              {remove.isPending ? null : <Trash2 aria-hidden />}
              Remover
            </Button>
          ) : null}
        </div>
      </div>
      <p className="mt-3 text-xs text-muted-foreground">PNG, JPG ou WebP, até 2 MB.</p>
      {error ? (
        <div className="mt-3">
          <FormAlert tone="error">{error}</FormAlert>
        </div>
      ) : null}
    </Section>
  );
}

function StatusSection({
  org,
  onSaved,
}: {
  org: OrganizationDetail;
  onSaved: (d: OrganizationDetail) => void;
}) {
  const { client } = useAuth();
  const [open, setOpen] = useState(false);
  const active = org.status === 'ACTIVE';
  const change = useMutation({
    meta: { handlesError: true },
    mutationFn: () =>
      client.request(`/organizations/${org.id}/status`, {
        method: 'POST',
        body: { status: active ? 'INACTIVE' : 'ACTIVE' },
        schema: organizationDetailSchema,
      }),
    onSuccess: (detail) => {
      setOpen(false);
      onSaved(detail);
      toast.success(detail.status === 'ACTIVE' ? 'Empresa reativada' : 'Empresa desativada');
    },
    onError: (err) => toast.error('Não foi possível mudar a situação', { description: errorMessage(err) }),
  });
  return (
    <Section title="Situação">
      <p className="text-sm text-muted-foreground">
        {active
          ? 'Empresa ativa: os usuários dela entram normalmente.'
          : 'Empresa desativada: ninguém dela consegue entrar. Os dados ficam guardados.'}
      </p>
      <Button
        variant={active ? 'outline' : 'primary'}
        size="sm"
        className="mt-4"
        onClick={() => setOpen(true)}
      >
        <Power aria-hidden />
        {active ? 'Desativar empresa' : 'Reativar empresa'}
      </Button>
      <ConfirmDialog
        open={open}
        onOpenChange={setOpen}
        title={active ? 'Desativar a empresa?' : 'Reativar a empresa?'}
        description={
          active
            ? `Todos os usuários de ${org.name} perdem o acesso na hora (as sessões abertas param de valer). Os dados não são apagados e você pode reativar depois.`
            : `Os usuários ativos de ${org.name} voltam a conseguir entrar.`
        }
        confirmLabel={active ? 'Desativar' : 'Reativar'}
        destructive={active}
        busy={change.isPending}
        onConfirm={() => change.mutate()}
      />
    </Section>
  );
}

function ProjectsSection({ org, onChanged }: { org: OrganizationDetail; onChanged: () => void }) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  const can = useCan();
  // "Abrir projeto" da Central de Empresas (T-145b; DEC-059): ?acao=abrir-projeto chega com o formulário aberto.
  const [params, setParams] = useSearchParams();
  const canOpen = can('central-empresas', 'create') && org.status === 'ACTIVE';
  const [editing, setEditing] = useState<Project | 'new' | null>(() =>
    params.get('acao') === 'abrir-projeto' && canOpen ? 'new' : null,
  );
  useEffect(() => {
    if (!params.has('acao')) return;
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('acao');
        return next;
      },
      { replace: true },
    );
  }, [params, setParams]);
  const [busy, setBusy] = useState(false);
  const [archiving, setArchiving] = useState<Project | null>(null);
  // Salvar o projeto como modelo (T-204b; DEC-070): só o Admin Fluxus.
  const [templating, setTemplating] = useState<Project | null>(null);
  const [templateBusy, setTemplateBusy] = useState(false);
  async function saveTemplate(project: Project, input: ProjectTemplateInput) {
    const template = await client.request(`/organizations/${org.id}/projects/${project.id}/template`, {
      method: 'POST',
      body: input,
      schema: projectTemplateSchema,
    });
    setTemplating(null);
    // A lista de modelos (tela de modelos e "Abrir projeto") já mostra o novo.
    await queryClient.invalidateQueries({ queryKey: ['project-templates'] });
    toast.success(`Modelo ${template.name} criado`, {
      description: `${plural(template.counts.objectives, 'objetivo', 'objetivos')} e ${plural(template.counts.kpis, 'KPI', 'KPIs')}, em Modelos de projeto.`,
    });
  }
  const archive = useMutation({
    meta: { handlesError: true },
    mutationFn: (p: Project) =>
      client.request(`/organizations/${org.id}/projects/${p.id}/archive`, {
        method: 'POST',
        schema: projectSchema,
      }),
    onSuccess: (p) => {
      setArchiving(null);
      onChanged();
      toast.success(`Projeto ${p.name} arquivado`);
    },
    onError: (err) => toast.error('Não foi possível arquivar', { description: errorMessage(err) }),
  });

  async function save(input: ProjectCreateInput) {
    const project =
      editing === 'new'
        ? await client.request(`/organizations/${org.id}/projects`, {
            method: 'POST',
            body: input,
            schema: projectSchema,
          })
        : await client.request(`/organizations/${org.id}/projects/${(editing as Project).id}`, {
            method: 'PATCH',
            body: input,
            schema: projectSchema,
          });
    setEditing(null);
    onChanged();
    toast.success(editing === 'new' ? `Projeto ${project.name} aberto` : 'Projeto salvo');
  }

  const active = org.projects.filter((p) => p.status === 'ACTIVE');
  const archived = org.projects.filter((p) => p.status === 'ARCHIVED');

  return (
    <Section
      title="Projetos BSC"
      action={
        canOpen ? (
          <Button size="sm" onClick={() => setEditing('new')}>
            <Plus aria-hidden />
            Abrir projeto
          </Button>
        ) : null
      }
    >
      {active.length === 0 ? (
        <EmptyState
          className="py-8"
          icon={FolderKanban}
          title="Nenhum projeto em andamento"
          description="Abra o projeto BSC da empresa com o horizonte estratégico, a fase e o consultor responsável."
        />
      ) : (
        <ul className="flex flex-col divide-y divide-border">
          {active.map((p) => (
            <ProjectRow
              key={p.id}
              project={p}
              onEdit={can('central-empresas', 'edit') ? () => setEditing(p) : undefined}
              onArchive={can('central-empresas', 'delete') ? () => setArchiving(p) : undefined}
              onSaveTemplate={can('central-empresas', 'admin') ? () => setTemplating(p) : undefined}
            />
          ))}
        </ul>
      )}
      {archived.length ? (
        <details className="mt-4 text-sm">
          <summary className="cursor-pointer font-semibold text-muted-foreground">
            Projetos arquivados ({archived.length})
          </summary>
          <ul className="mt-2 flex flex-col divide-y divide-border">
            {archived.map((p) => (
              <ProjectRow
                key={p.id}
                project={p}
                onSaveTemplate={can('central-empresas', 'admin') ? () => setTemplating(p) : undefined}
              />
            ))}
          </ul>
        </details>
      ) : null}

      <Dialog
        open={editing !== null}
        onOpenChange={(open) => !open && setEditing(null)}
        busy={busy}
        title={editing === 'new' ? 'Abrir projeto BSC' : 'Editar projeto'}
        description={editing === 'new' ? `Projeto de ${org.name}.` : undefined}
      >
        {editing !== null ? (
          <ProjectForm
            initial={editing === 'new' ? undefined : editing}
            organizationId={editing === 'new' ? org.id : undefined}
            submitLabel={editing === 'new' ? 'Abrir projeto' : 'Salvar projeto'}
            onSubmit={save}
            onCancel={() => setEditing(null)}
            onBusyChange={setBusy}
          />
        ) : null}
      </Dialog>
      <Dialog
        open={templating !== null}
        onOpenChange={(open) => !open && setTemplating(null)}
        busy={templateBusy}
        title="Salvar como modelo"
        description={
          templating
            ? `Guarda a estrutura de ${templating.name} — perspectivas, objetivos corporativos, relações e KPIs, sem metas, resultados, áreas ou pessoas — para abrir projetos de outras empresas a partir dela.`
            : undefined
        }
      >
        {templating ? (
          <TemplateForm
            initial={{ name: templating.name, description: null }}
            submitLabel="Salvar modelo"
            onSubmit={(input) => saveTemplate(templating, input)}
            onCancel={() => setTemplating(null)}
            onBusyChange={setTemplateBusy}
          />
        ) : null}
      </Dialog>
      <ConfirmDialog
        open={archiving !== null}
        onOpenChange={(open) => !open && setArchiving(null)}
        title="Arquivar o projeto?"
        description={`${archiving?.name ?? ''} sai das listas de projetos ativos. O histórico é preservado e continua disponível nos arquivados.`}
        confirmLabel="Arquivar projeto"
        destructive
        busy={archive.isPending}
        onConfirm={() => archiving && archive.mutate(archiving)}
      />
    </Section>
  );
}

function ProjectRow({
  project,
  onEdit,
  onArchive,
  onSaveTemplate,
}: {
  project: Project;
  onEdit?: () => void;
  onArchive?: () => void;
  onSaveTemplate?: () => void;
}) {
  return (
    <li className="flex flex-col gap-3 py-3 sm:flex-row sm:items-center">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="font-semibold">{project.name}</p>
          <Badge variant={project.status === 'ACTIVE' ? 'brand' : 'neutral'}>
            {project.status === 'ACTIVE' ? PROJECT_PHASE_LABELS[project.phase] : 'Arquivado'}
          </Badge>
        </div>
        <p className="mt-0.5 flex flex-wrap items-center gap-x-3 text-sm text-muted-foreground">
          <span>
            Horizonte {project.horizonStart}–{project.horizonEnd}
          </span>
          <span className="inline-flex items-center gap-1">
            <UserRound className="size-3.5" aria-hidden />
            {project.consultant?.name ?? 'Sem consultor definido'}
          </span>
        </p>
      </div>
      {onEdit || onArchive || onSaveTemplate ? (
        <div className="flex flex-wrap gap-2">
          {onEdit ? (
            <Button variant="outline" size="sm" onClick={onEdit} aria-label={`Editar ${project.name}`}>
              <Pencil aria-hidden />
              Editar
            </Button>
          ) : null}
          {onSaveTemplate ? (
            <Button
              variant="outline"
              size="sm"
              onClick={onSaveTemplate}
              aria-label={`Salvar como modelo: ${project.name}`}
            >
              <LayoutTemplate aria-hidden />
              Salvar como modelo
            </Button>
          ) : null}
          {onArchive ? (
            <Button variant="ghost" size="sm" onClick={onArchive} aria-label={`Arquivar ${project.name}`}>
              <Archive aria-hidden />
              Arquivar
            </Button>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}
